import React, { useState, useEffect, useRef } from "react";
import { rawSql, getStats } from "../api";

const ORANGE = "#c4622d";

const DEFAULT_SQL = `SELECT rc.first_name, rc.last_name, rc.gender, COUNT(*) as wins
FROM results res
JOIN runners r ON r.runner_id = res.runner_id
JOIN runners rc ON rc.runner_id = r.canonical_runner_id
WHERE res.gender_position = 1
GROUP BY rc.runner_id, rc.first_name, rc.last_name, rc.gender
ORDER BY wins DESC
LIMIT 10`;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function ProgressBar({ value, total }) {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div style={styles.barTrack}>
      <div style={{ ...styles.barFill, width: `${pct}%` }} />
    </div>
  );
}

function ScrapeProgress({ compact }) {
  const [stats, setStats] = useState(null);
  const intervalRef = useRef(null);

  const load = () => getStats().then(setStats).catch(() => {});

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 10000);
    return () => clearInterval(intervalRef.current);
  }, []);

  if (!stats || stats.error) return null;

  const { events, runners_total, results_total, last_scraped_event } = stats;
  const eventsScraped = events?.scraped ?? 0;
  const eventsTotal = events?.total ?? 0;
  const eventsPct = eventsTotal > 0 ? Math.round((eventsScraped / eventsTotal) * 100) : 0;

  if (compact) {
    return (
      <div style={styles.progressCompact}>
        <div style={styles.compactRow}>
          <span style={styles.compactLabel}>Events</span>
          <span style={styles.compactVal}>{eventsScraped.toLocaleString()}/{eventsTotal.toLocaleString()}</span>
          <div style={{ width: "48px", flexShrink: 0 }}><ProgressBar value={eventsScraped} total={eventsTotal} /></div>
          <span style={styles.compactPct}>{eventsPct}%</span>
          <span style={styles.compactSep}>·</span>
          <span style={styles.compactLabel}>Runners</span>
          <span style={styles.compactVal}>{(runners_total ?? 0).toLocaleString()}</span>
          <span style={styles.compactSep}>·</span>
          <span style={styles.compactLabel}>Results</span>
          <span style={styles.compactVal}>{(results_total ?? 0).toLocaleString()}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.progress}>
      <div style={styles.progressTitle}>Scrape Progress</div>
      <div style={styles.statGrid}>
        <div style={styles.statBox}>
          <div style={styles.statVal}>{eventsScraped.toLocaleString()}</div>
          <div style={styles.statLabel}>Events scraped</div>
          <ProgressBar value={eventsScraped} total={eventsTotal} />
          <div style={styles.statSub}>{eventsPct}% of {eventsTotal.toLocaleString()}</div>
        </div>
        <div style={styles.statBox}>
          <div style={styles.statVal}>{(runners_total ?? 0).toLocaleString()}</div>
          <div style={styles.statLabel}>Runners</div>
        </div>
        <div style={styles.statBox}>
          <div style={styles.statVal}>{(results_total ?? 0).toLocaleString()}</div>
          <div style={styles.statLabel}>Results</div>
        </div>
      </div>
      {last_scraped_event && (
        <div style={styles.lastEvent}>
          Last scraped: <strong>{last_scraped_event.name}</strong>
          <span style={styles.lastEventMeta}> — {last_scraped_event.date}</span>
        </div>
      )}
      <div style={styles.refreshNote}>Auto-refreshes every 10s</div>
    </div>
  );
}

export default function SqlConsole() {
  const isMobile = useIsMobile();
  const [sql, setSql] = useState(DEFAULT_SQL);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(null);

  const run = async () => {
    const q = sql.trim();
    if (!q || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    const t0 = Date.now();
    try {
      const data = await rawSql(q);
      setResult(data);
      setElapsed(Date.now() - t0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      run();
    }
  };

  return (
    <div style={styles.shell}>
      <header style={styles.header}>
        <span style={styles.logo}>SQL Console</span>
        <span style={styles.hint}>{isMobile ? "Tap Run to execute" : "Ctrl+Enter to run"}</span>
      </header>

      {/* Mobile: compact progress bar at top */}
      {isMobile && <ScrapeProgress compact />}

      <div style={isMobile ? styles.bodyMobile : styles.body}>
        <div style={isMobile ? styles.leftColMobile : styles.leftCol}>
          <div style={isMobile ? styles.editorRowMobile : styles.editorRow}>
            <textarea
              style={{ ...styles.editor, minHeight: isMobile ? "100px" : "120px", fontSize: isMobile ? "1rem" : "0.85rem", resize: isMobile ? "none" : "vertical" }}
              value={sql}
              onChange={(e) => setSql(e.target.value)}
              onKeyDown={handleKey}
              spellCheck={false}
            />
            <button
              style={{ ...styles.runBtn, background: loading ? "#ddd" : ORANGE, width: isMobile ? "100%" : "auto", cursor: loading ? "default" : "pointer" }}
              onClick={run}
              disabled={loading}
            >
              {loading ? "Running…" : "Run"}
            </button>
          </div>

          {error && (
            <div style={styles.error}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {result && (
            <div style={styles.resultArea}>
              <div style={styles.resultMeta}>
                {result.rows.length} row{result.rows.length !== 1 ? "s" : ""}
                {elapsed != null && <span style={styles.elapsed}> — {elapsed}ms</span>}
              </div>
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {result.columns.map((col) => (
                        <th key={col} style={styles.th}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((row, i) => (
                      <tr key={i} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                        {row.map((cell, j) => (
                          <td key={j} style={styles.td}>
                            {cell === null || cell === undefined
                              ? <span style={styles.null}>NULL</span>
                              : String(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Desktop: sidebar */}
        {!isMobile && (
          <div style={styles.rightCol}>
            <ScrapeProgress />
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  shell: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    background: "#f7f7f8",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    background: "#fff",
    borderBottom: "1px solid #e1e1e1",
    flexShrink: 0,
  },
  logo: {
    fontWeight: 700,
    fontSize: "1rem",
    color: ORANGE,
  },
  hint: {
    fontSize: "0.8rem",
    color: "#aaa",
  },
  body: {
    display: "grid",
    gridTemplateColumns: "1fr 280px",
    flex: 1,
    overflow: "hidden",
  },
  bodyMobile: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
  },
  leftCol: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRight: "1px solid #e1e1e1",
  },
  leftColMobile: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  rightCol: {
    overflowY: "auto",
    background: "#fff",
  },
  editorRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: "12px",
    padding: "16px 24px",
    background: "#fff",
    borderBottom: "1px solid #e1e1e1",
    flexShrink: 0,
    alignItems: "stretch",
  },
  editorRowMobile: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px 16px",
    background: "#fff",
    borderBottom: "1px solid #e1e1e1",
    flexShrink: 0,
  },
  editor: {
    fontFamily: "'Fira Mono', 'Consolas', monospace",
    fontSize: "0.85rem",
    border: "1px solid #d1d1d1",
    borderRadius: "6px",
    padding: "10px 12px",
    resize: "vertical",
    outline: "none",
    lineHeight: 1.5,
  },
  runBtn: {
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "0 24px",
    fontWeight: 600,
    fontSize: "0.9rem",
    cursor: "pointer",
    alignSelf: "start",
    height: "38px",
  },
  error: {
    margin: "12px 16px 0",
    padding: "12px 16px",
    background: "#fff0f0",
    border: "1px solid #ffcccc",
    borderRadius: "6px",
    fontSize: "0.82rem",
    color: "#c00",
    fontFamily: "'Fira Mono', 'Consolas', monospace",
    whiteSpace: "pre-wrap",
  },
  resultArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    padding: "12px 16px",
    gap: "8px",
  },
  resultMeta: {
    fontSize: "0.8rem",
    color: "#888",
    flexShrink: 0,
  },
  elapsed: { color: "#bbb" },
  tableWrapper: {
    flex: 1,
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e1e1e1",
    borderRadius: "6px",
    background: "#fff",
    WebkitOverflowScrolling: "touch",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.82rem",
  },
  th: {
    position: "sticky",
    top: 0,
    background: "#f8f8f8",
    textAlign: "left",
    padding: "8px 12px",
    borderBottom: "2px solid #ddd",
    fontWeight: 600,
    color: "#444",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "7px 12px",
    borderBottom: "1px solid #f0f0f0",
    color: "#333",
    whiteSpace: "nowrap",
  },
  trEven: { background: "#fff" },
  trOdd: { background: "#fafafa" },
  null: { color: "#ccc", fontStyle: "italic" },

  // Desktop progress sidebar
  progress: {
    padding: "20px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  progressTitle: {
    fontWeight: 700,
    fontSize: "0.85rem",
    color: "#333",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  statGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  statBox: {
    background: "#f7f7f8",
    borderRadius: "8px",
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  statVal: {
    fontSize: "1.4rem",
    fontWeight: 700,
    color: ORANGE,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: "0.75rem",
    color: "#888",
    marginBottom: "6px",
  },
  statSub: {
    fontSize: "0.72rem",
    color: "#aaa",
    marginTop: "4px",
  },
  barTrack: {
    height: "4px",
    background: "#e1e1e1",
    borderRadius: "2px",
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    background: ORANGE,
    borderRadius: "2px",
    transition: "width 0.4s ease",
  },
  lastEvent: {
    fontSize: "0.78rem",
    color: "#555",
    lineHeight: 1.4,
    padding: "10px 14px",
    background: "#f7f7f8",
    borderRadius: "8px",
  },
  lastEventMeta: { color: "#aaa" },
  refreshNote: {
    fontSize: "0.7rem",
    color: "#ccc",
    textAlign: "center",
  },

  // Mobile compact progress — single scrollable line
  progressCompact: {
    padding: "6px 16px",
    background: "#fff",
    borderBottom: "1px solid #e1e1e1",
    flexShrink: 0,
    overflowX: "auto",
  },
  compactRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
  },
  compactLabel: {
    color: "#aaa",
    flexShrink: 0,
  },
  compactVal: {
    color: "#333",
    fontWeight: 600,
    flexShrink: 0,
  },
  compactPct: {
    color: ORANGE,
    fontWeight: 600,
    flexShrink: 0,
    fontSize: "0.72rem",
  },
  compactSep: {
    color: "#ddd",
    flexShrink: 0,
  },
};
